import { useCallback } from 'react';
import { atom, useRecoilState } from 'recoil';
import { OverlappingFeatureAction } from 'features/mapping/mapping.types';

export type OverlappingFeaturesState = {
  features: OverlappingFeatureAction[];
  activeIndex: number;
};

type UseOverlappingFeaturesState = {
  features: OverlappingFeatureAction[];
  activeFeature?: OverlappingFeatureAction;
  activeIndex: number;
  hasOverlappingFeatures: boolean;
  setOverlappingFeatures: (stack: OverlappingFeaturesState) => void;
  clearOverlappingFeatures: () => void;
  removeFeatureAtIndex: (index: number) => void;
  selectIndex: (index: number) => void;
};

const DEFAULT_OVERLAPPING_FEATURES_STATE: OverlappingFeaturesState = {
  features: [],
  activeIndex: 0,
};

const overlappingFeaturesAtom = atom({
  key: 'OVERLAPPING_FEATURES_STATE',
  default: DEFAULT_OVERLAPPING_FEATURES_STATE,
});

export const useOverlappingFeaturesState = (): UseOverlappingFeaturesState => {
  const [state, setState] = useRecoilState(overlappingFeaturesAtom);

  const setOverlappingFeatures = useCallback(
    (stack: OverlappingFeaturesState) => {
      setState(stack);
    },
    [setState],
  );

  const clearOverlappingFeatures = useCallback(() => {
    setState(DEFAULT_OVERLAPPING_FEATURES_STATE);
  }, [setState]);

  const selectIndex = useCallback(
    (index: number) => {
      setState((prev) => {
        if (index < 0 || index >= prev.features.length) return prev;
        return { ...prev, activeIndex: index };
      });
    },
    [setState],
  );

  const removeFeatureAtIndex = useCallback(
    (index: number) => {
      setState((prev) => {
        const newFeatures = prev.features.filter((_, i) => i !== index);
        if (newFeatures.length === 0) {
          return DEFAULT_OVERLAPPING_FEATURES_STATE;
        }
        let newActiveIndex = prev.activeIndex;
        if (index < prev.activeIndex) {
          newActiveIndex -= 1;
        } else if (index === prev.activeIndex) {
          newActiveIndex = Math.min(newActiveIndex, newFeatures.length - 1);
        }
        return { features: newFeatures, activeIndex: newActiveIndex };
      });
    },
    [setState],
  );

  const activeFeature = state.features[state.activeIndex];

  return {
    features: state.features,
    activeFeature,
    activeIndex: state.activeIndex,
    hasOverlappingFeatures: state.features.length > 1,
    setOverlappingFeatures,
    clearOverlappingFeatures,
    removeFeatureAtIndex,
    selectIndex,
  };
};
